import toInteger from 'lodash/toInteger';
import DirectorySyncOptionsModel from 'models/directorySyncOptions';
import RiskMatrix from 'models/RiskMatrix';
import WorkingHoursPattern from 'models/WorkingHoursPattern';
import AppError from 'utils/appError';
import { toBool } from '../utils/string';
import { PlannerBusinessScenario } from './plannerBusinessScenario';
import { SelectedSitesSteps } from './SelectedSitesSteps';

//general settings
export const AutoUserLicense = 'AutoUserLicense';
export const DefLanguageCode = 'DefLanguageCode';
export const AuditYearStart = 'AuditYearStart';
export const RiskScoreDef = 'RiskScoreDef';
export const WorkingHours = 'WorkingHours';
export const OrgLanguageCodes = 'OrgLanguageCodes';
export const ModuleRisks = 'ModuleRisks';
export const ModuleOrg = 'ModuleOrg';
export const ModuleThemes = 'ModuleThemes';
export const ModuleControls = 'ModuleControls';
export const ModuleAssets = 'ModuleAssets';
export const ModuleStandards = 'ModuleStandards';
export const ModuleProcessingActivities = 'ModuleProcessingActivities';
export const ModuleAudit = 'ModuleAudit';
// Opt-in module: absent or false means off, unlike its siblings above.
export const ModuleProjects = 'ModuleProjects';
export const LibraryOpenInModal = 'LibraryOpenInModal';
export const ShowISOElementAds = 'ShowISOElementAds';
export const LegacyOutlookSeriesEnabled = 'LegacyOutlookSeriesEnabled';
export const Risk3dMatrix = 'Risk3dMatrix';
export const PreviewFeatureToggles = 'PreviewFeatureToggles';
export const PrivacyFrameworkSetting = 'PrivacyFramework';
// When true, the task deep-link scene skips the client-side owner/assignee gate for normal users.
// Enforced in the client only; the API stores the record but never reads it.
export const DisableTaskLinkAuthView = 'DisableTaskLinkAuthView';

//report settings
//ReportStorageList, ReportTemplateLink and AuditReportStorageList are no longer read: the categories and the template
//of the general and audit reports are their output location rows. The constants stay so stored values still parse
//until the next release deletes them.
export const ReportStorageList = 'ReportStorageList';
export const ReportTemplateLink = 'ReportTemplateLink';
export const ReportDefaultStorageBehavior = 'ReportDefaultStorageBehavior';
export const ReportDefaultOutputFormat = 'ReportDefaultOutputFormat';
export const ReportAllowUserSettingOverride = 'ReportAllowUserSettingOverride';
export const ReportKeepWordNextToPDF = 'ReportKeepWordNextToPDF';
export const AuditReportStorageList = 'AuditReportStorageList';

//auth settings
export const AzureAD = 'AzureAD';
export const AzureADGroupSync = 'AzureADGroupSync';
//the options of the user and team synchronization (models/directorySyncOptions); not propagated to org units
export const DirectorySyncOptions = 'DirectorySyncOptions';
//the directory of a Google Workspace or a Nextcloud organization has been connected and verified: the counterpart
//of AzureAD (the Microsoft Entra ID integration consent), written by the API on verification and removal
export const GoogleWorkspaceDirectory = 'GoogleWorkspaceDirectory';
export const NextcloudDirectory = 'NextcloudDirectory';
export const OutlookTasksAdvanced = 'OutlookTasksAdvanced';
export const OutlookTasksBasic = 'OutlookTasksBasic';
export const SharepointLibraries = 'SharepointLibraries';
export const SharepointPages = 'SharepointPages';
export const SharepointReportsAndPublishing = 'SharepointReports';
export const SharepointSearch = 'SharepointSearch';
export const TeamsKB = 'TeamsKB';
export const TeamsTasks = 'TeamsTasks';
export const PowerBI = 'PowerBI';
export const PowerBIService = 'PowerBIService';
export const TeamsKBTaskTemplate = 'TeamsKBTaskTemplate';
export const TeamsCollaboration = 'TeamsCollaboration';
export const Planner = 'Planner';
export const AzureADPhotos = 'AzureADPhotos';
export const APPSelectedSites = 'APPSelectedSites';
export const SVCSelectedSites = 'SVCSelectedSites';
export const APISelectedSites = 'APISelectedSites';
export const MicrosoftCloudSecureScore = 'MicrosoftCloudSecureScore';
export const Microsoft365SecureScore = 'Microsoft365SecureScore';
export const MicrosoftIdentitySecureScore = 'MicrosoftIdentitySecureScore';
export const MicrosoftDevicesSecureScore = 'MicrosoftDevicesSecureScore';

//automation settings
export const PowerAutomateSharePoint = 'PowerAutomateSharePoint';
export const AIEnabled = 'AIEnabled';
//the display name of the AI assistant; empty means the localized default (see components/Copilot/aiAssistantName)
export const AIAssistantName = 'AIAssistantName';
//true when the organization's own AI models replace the ISOPlanner models (all three profiles at once);
//written only through PUT ai/models/enabled (services/Api/aiModelService), never with apiSetSetting
export const AICustomModelsEnabled = 'AICustomModelsEnabled';
//whether the assistant may research the internet; only consulted while the organization's own models are in use
export const AIInternetSearchEnabled = 'AIInternetSearchEnabled';
//whether the assistant may run code in the AI provider's sandbox for the copilot chat (default true)
export const AICodeExecutionEnabled = 'AICodeExecutionEnabled';
//whether the AI re-evaluates audit criteria on its own when one of their sources changes (default true)
export const AICriteriaAutoSync = 'AICriteriaAutoSync';
//the organization's own switch of the SkillControl integration (Settings > Integration > SkillControl); only consulted
//while the preview toggle skillControl is on (models/copilot/copilotPreviewFeatureToggles isSkillControlEnabled)
export const SkillControlEnabled = 'SkillControlEnabled';

//notification settings
export const NotificationEmailSignature = 'NotificationEmailSignature';

//setting enums
export enum SelectedSitesStep {
  None = 0,
  Auth = 1,
  Config = 2,
  Enabled = 3,
}

export class Settings {
  private static _workingHoursPattern: WorkingHoursPattern | undefined;

  items: Setting[];

  constructor() {
    this.items = [];
  }

  //Privately cached setting for fast access
  getStaticWorkingHours(): WorkingHoursPattern {
    if (Settings._workingHoursPattern) return Settings._workingHoursPattern;
    Settings._workingHoursPattern = this.get(WorkingHours);

    return Settings._workingHoursPattern ?? new WorkingHoursPattern();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get(name: string): any {
    const setting = this.items.find((s) => {
      return s.settingName === name;
    });
    if (setting) {
      return setting.getValue();
    } else {
      return undefined;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  set(name: string, value: any) {
    const setting = this.items.find((s) => {
      return s.settingName === name;
    });
    if (setting) {
      setting.setValue(setting.settingName, value);
    } else {
      return undefined;
    }
  }

  clone(): Settings {
    const newSettings = new Settings();
    newSettings.items = this.items.map((s) => s.clone());

    return newSettings;
  }
}

export default class Setting {
  settingName: string;

  settingValue: string | undefined;

  constructor() {
    this.settingName = '';
    this.settingValue = undefined;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValue(): any {
    try {
      const boolSettings = [
        AutoUserLicense,
        SharepointLibraries,
        SharepointPages,
        SharepointReportsAndPublishing,
        SharepointSearch,
        TeamsKB,
        TeamsTasks,
        TeamsCollaboration,
        ModuleRisks,
        ModuleOrg,
        ModuleThemes,
        ModuleControls,
        ModuleAssets,
        ModuleStandards,
        ModuleProcessingActivities,
        ModuleAudit,
        ModuleProjects,
        OutlookTasksAdvanced,
        OutlookTasksBasic,
        AzureAD,
        MicrosoftCloudSecureScore,
        Microsoft365SecureScore,
        MicrosoftIdentitySecureScore,
        MicrosoftDevicesSecureScore,
        AzureADGroupSync,
        GoogleWorkspaceDirectory,
        NextcloudDirectory,
        PowerAutomateSharePoint,
        LibraryOpenInModal,
        ReportAllowUserSettingOverride,
        ReportKeepWordNextToPDF,
        ShowISOElementAds,
        PowerBI,
        PowerBIService,
        LegacyOutlookSeriesEnabled,
        AIEnabled,
        AzureADPhotos,
        Risk3dMatrix,
        DisableTaskLinkAuthView,
        AICustomModelsEnabled,
        AIInternetSearchEnabled,
        AICodeExecutionEnabled,
        AICriteriaAutoSync,
        SkillControlEnabled,
      ];

      const intSettings = [
        ReportStorageList,
        AuditReportStorageList,
        ReportTemplateLink,
        ReportDefaultStorageBehavior,
        ReportDefaultOutputFormat,
        PrivacyFrameworkSetting,
      ];

      if (boolSettings.includes(this.settingName)) {
        return toBool(this.settingValue);
      }

      if (intSettings.includes(this.settingName)) {
        return toInteger(this.settingValue);
      }

      switch (this.settingName) {
        case TeamsKBTaskTemplate:
          return Number.parseInt(this.settingValue ?? '');
        case DefLanguageCode:
        case AuditYearStart:
        case NotificationEmailSignature:
        case AIAssistantName:
          return this.settingValue;
        case RiskScoreDef:
          const matrix = new RiskMatrix();
          matrix.fromJSON(this.settingValue);

          return matrix;
        case WorkingHours:
          const workingHours: WorkingHoursPattern = new WorkingHoursPattern();
          workingHours.fromJSON(this.settingValue);

          return workingHours;
        case OrgLanguageCodes:
        case PreviewFeatureToggles:
          if (!this.settingValue) return null;

          return JSON.parse(this.settingValue);
        case Planner:
          const plannerBusinessScenario = new PlannerBusinessScenario();
          plannerBusinessScenario.fromJSON(this.settingValue);

          return plannerBusinessScenario;
        case DirectorySyncOptions:
          return DirectorySyncOptionsModel.fromJSON(this.settingValue);
        case SVCSelectedSites:
        case APPSelectedSites:
        case APISelectedSites:
          return new SelectedSitesSteps(this.settingValue);
        default:
          throw new AppError('Setting not found: ' + this.settingName);
      }
    } catch (err) {
      throw err;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue(setting: string, value: any) {
    try {
      const jsonSettings = [
        RiskScoreDef,
        WorkingHours,
        OrgLanguageCodes,
        PreviewFeatureToggles,
        Planner,
        SVCSelectedSites,
        APPSelectedSites,
        APISelectedSites,
        DirectorySyncOptions,
      ];

      const stringSettings = [
        AutoUserLicense,
        DefLanguageCode,
        AuditYearStart,
        SharepointLibraries,
        SharepointPages,
        SharepointReportsAndPublishing,
        SharepointSearch,
        TeamsKB,
        TeamsTasks,
        TeamsCollaboration,
        TeamsKBTaskTemplate,
        ModuleRisks,
        ModuleOrg,
        ModuleThemes,
        ModuleControls,
        ModuleAssets,
        ModuleStandards,
        ModuleProcessingActivities,
        ModuleAudit,
        ModuleProjects,
        OutlookTasksAdvanced,
        OutlookTasksBasic,
        AzureAD,
        MicrosoftCloudSecureScore,
        Microsoft365SecureScore,
        MicrosoftIdentitySecureScore,
        MicrosoftDevicesSecureScore,
        AzureADGroupSync,
        GoogleWorkspaceDirectory,
        NextcloudDirectory,
        PowerAutomateSharePoint,
        LibraryOpenInModal,
        ReportStorageList,
        AuditReportStorageList,
        ReportTemplateLink,
        ReportDefaultStorageBehavior,
        ReportDefaultOutputFormat,
        ReportAllowUserSettingOverride,
        ReportKeepWordNextToPDF,
        ShowISOElementAds,
        PowerBI,
        PowerBIService,
        LegacyOutlookSeriesEnabled,
        AIEnabled,
        AIAssistantName,
        Planner,
        AzureADPhotos,
        Risk3dMatrix,
        PrivacyFrameworkSetting,
        NotificationEmailSignature,
        DisableTaskLinkAuthView,
        AICustomModelsEnabled,
        AIInternetSearchEnabled,
        AICodeExecutionEnabled,
        AICriteriaAutoSync,
        SkillControlEnabled,
      ];

      this.settingName = setting;

      if (jsonSettings.includes(setting)) {
        switch (this.settingName) {
          case APISelectedSites:
          case SVCSelectedSites:
          case APPSelectedSites:
            this.settingValue = (value as SelectedSitesSteps).toJSON();
            break;
          case RiskScoreDef:
            this.settingValue = (value as RiskMatrix).toJSON();
            break;
          case WorkingHours:
            this.settingValue = (value as WorkingHoursPattern).toJSON();
            break;
          case DirectorySyncOptions:
            this.settingValue = (value as DirectorySyncOptionsModel).toJSON();
            break;
          case OrgLanguageCodes:
          case PreviewFeatureToggles:
            this.settingValue = JSON.stringify(value);
            break;
          case Planner:
            this.settingValue = (value as PlannerBusinessScenario).toJSON();
            break;
          default:
            break;
        }
      } else if (stringSettings.includes(setting)) {
        this.settingValue = value.toString();
      } else {
        throw new AppError('Setting not found: ' + this.settingName);
      }
    } catch (err) {
      throw err;
    }
  }

  clone(): Setting {
    const newSetting = new Setting();
    newSetting.settingName = this.settingName;
    newSetting.settingValue = this.settingValue;

    return newSetting;
  }
}
